import { Badge, Button, Card, CardBody, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Building,
  CheckCircle,
  Circle,
  CircleDot,
  Download,
  FileText,
  ChevronRight,
  ExternalLink,
  Layers,
  ChevronLeft,
  Users,
  Goal,
  Info,
  ShieldCheck,
  XCircle,
  Grid2X2,
  MapPin,
  UserCheck2,
  Banknote,
  ArrowRight,
  Building2,
  LucideIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { IProject, IProjectStage } from "../core/_models";
import { buildNavigationState, getStageRedirect, stageCompletedCode, useSmartNavigation } from '../../../../utils/config';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { GeoLocationPopUp } from "../../Dashboard/components/geoLocationPopUp";
import CommonModal from "../../apps/components/CustomCommonComingSoonModal";
import { IProjectEvaluationSummeryData, UserScore } from "../../core/_models";
import ManageTeamMembersModal from "./common/ManageTeamMembersModal";
import { KTIcon } from "../../../../_theme/helpers";
import { downloadBusinessCaseReport } from "../core/_request";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { getApiErrorMessage } from "../../../../utils/utils";
import { usePermission } from "../../auth";
import PermissionGate from "../../apps/components/UI/PermissionGate";

type StageGateInfo = {
  [key: number]: {
    stage_id: string;
    name: string;
    description: string;
    icon: LucideIcon;
    color: string;
  };
};

type Status = "completed" | "current" | "upcoming";

type Props = {
  project: IProject;
  stageGateInfo: StageGateInfo;
  stages: IProjectStage[];
  getStatusIcon: (status: Status) => React.ComponentType<any>;
  onManageEvaluator?: (projectId: string) => void;
  setShowManageEvaluator: (show: boolean) => void;
  summeryData?: IProjectEvaluationSummeryData | null
};

// Constants
const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  secondary: '#64748b',
  purple: '#8b5cf6',
  warning: '#f59e0b'
} as const;

const REPORT_COLORS = {
  plan: '#8b5cf6',
  draft: '#8b5cf6',
  final: '#8b5cf6',
  executive: '#8b5cf6'
} as const;

const stageColors = [
  "#049576", "#b20404", "#c49702", "#a30664", "#380862",
  "#037ca4", "#609a04", "#a86303", "#6D597A", "#2166a2"
];

const statusConfig = {
  completed: {
    color: COLORS.success,
    icon: CheckCircle,
    label: 'Completed',
    badgeClass: 'bg-success bg-opacity-10 text-success border-success'
  },
  current: {
    color: COLORS.primary,
    icon: CircleDot,
    label: 'In Progress',
    badgeClass: 'bg-opacity-0 text-primary border-primary'
  },
  upcoming: {
    color: COLORS.secondary,
    icon: Circle,
    label: 'Locked',
    badgeClass: 'bg-secondary bg-opacity-10 text-gray-600 border-secondary'
  }
} as const;

// Utility function
const getLighterColor = (color: string, opacity: number = 0.1): string => {
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

// Sub-components
const TimelineCircle: React.FC<{ color: string; icon: any }> = ({ color, icon: Icon }) => (
  <div
    className="d-none d-md-flex align-items-center justify-content-center rounded-circle shadow-sm position-absolute start-0 top-50 translate-middle-y"
    style={{
      width: '42px',
      height: '42px',
      background: `${color}20`,
      border: `2px solid ${color}`,
    }}
  >
    <Icon size={20} color={color} />
  </div>
);

const HorizontalConnector: React.FC<{ color: string }> = ({ color }) => (
  <div
    className="position-absolute d-none d-md-block end-0 top-50 translate-middle-y"
    style={{
      height: '2px',
      width: 'calc(100% - 21px)',
      background: color,
      opacity: 0.3
    }}
  />
);

const StageNumberBox: React.FC<{ icon: any; color: string; gateNum: number; lightBg: string; mediumBg: string }> =
  ({ icon: Icon, color, gateNum, lightBg, mediumBg }) => (
    <div
      className="d-flex flex-column align-items-center justify-content-center rounded-3 shadow-sm flex-shrink-0"
      style={{
        width: '58px',
        height: '58px',
        background: `linear-gradient(135deg, ${lightBg} 0%, ${mediumBg} 100%)`,
        border: `2px solid ${color}`,
      }}
    >
      <Icon size={20} color={color} />
      <span className="fw-bold mt-1" style={{ color, fontSize: '16px' }}>
        S{gateNum}
      </span>
    </div>
  );

const StatusBadge: React.FC<{ badgeClass: string; label: string }> = ({ badgeClass, label }) => (
  <Badge
    bg="white"
    className={`px-3 py-2 rounded-pill shadow-sm border position-absolute end-0 top-0 me-4 mt-4 mb-4 ${badgeClass}`}
    style={{
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }}
  >
    {label}
  </Badge>
);

const ActionBadge: React.FC<{
  to?: string;
  onClick?: () => void;
  icon?: any;
  text: string;
  color: string;
  external?: boolean;
  download?: boolean;
}> = ({ to, onClick, icon: Icon, text, color, external = false, download = false }) => {

  const location = useLocation();

  const content = (
    <>
      {Icon && <Icon size={12} />}
      <span className="d-sm-inline ms-1">{text}</span>
      {external && <ExternalLink size={12} className="ms-1" />}
      {download && <Download size={12} className="ms-1" />}
    </>
  );

  const bgStyle = {
    background: `linear-gradient(135deg, ${getLighterColor(color, 0.1)} 0%, ${getLighterColor(color, 0.2)} 100%)`,
    border: `1px solid ${color}`,
  };

  const baseClass = "badge text-decoration-none px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1 shadow-sm";

  if (to) {
    return (
      <Link
        to={to}
        state={{
          from: location.state?.from || location.pathname, //  preserve origin
        }}
        target={download ? "_blank" : undefined}
        className={baseClass}
        style={{ ...bgStyle, color }}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      className={baseClass}
      style={{ ...bgStyle, color, cursor: 'pointer' }}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {content}
    </div>
  );
};

const Actions: React.FC<{ stageData: IProjectStage, project: IProject, status: string }> = ({ stageData, project, status }) => {
  const reportDocs = useMemo(() => {
    if (!stageData?.report_documents) return [];
    return Object.entries(stageData.report_documents)
      .filter(([_, doc]: [string, any]) => doc?.doc_file_path)
      .map(([key, doc]: [string, any]) => ({
        name: doc.doc_name || key,
        path: doc.doc_file_path
      }));
  }, [stageData]);

  if (reportDocs.length === 0 && !stageData.doc_file_path && !stageData.doc2_file_path) return null;

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 justify-content-start justify-content-lg-end mt-0 mt-lg-9">
      {stageData.dk_stage === "project_stage_3" && stageData.dn_stage_status === "Approved" && (
        <>
          <ActionBadge
            to={`/manage-project/elevator-pitch/${project.project_id}/${location?.search}`}
            text="Draft Elevator Pitch"
            color={REPORT_COLORS.draft}
            external
          />
          <Link to={`/manage-project/single-page-report/${project.project_id}/${location?.search}`}>
            <Building size={16} />
          </Link>
        </>
      )}
      {stageData.doc_file_path && (
        <ActionBadge
          to={stageData.doc_file_path}
          text={stageData.doc_name || ''}
          color={COLORS.primary}
          download
        />
      )}
      {stageData.doc2_file_path && (
        <ActionBadge
          to={stageData.doc2_file_path}
          text={stageData.doc2_name || ''}
          color={COLORS.primary}
          download
        />
      )}
      {reportDocs.map((doc, index) => (
        <ActionBadge
          key={index}
          to={doc.path}
          text={doc.name}
          color={COLORS.primary}
          download
        />
      ))}
    </div>
  );
};

function StackedAvatarList({ items }: {
  items: UserScore[];
}) {
  const visibleItems = items
  const extraCount = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {visibleItems.map((item, idx) => (
        <OverlayTrigger
          key={idx}
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${idx}`}>
              <div className="text-start">
                Name: <strong>{item.name}</strong>
                <br />
                Status: <strong>{item.bg === 'success' ? 'Completed' : item.bg === 'danger' ? 'Overdue' : item.bg === 'warning' ? 'Pending' : 'In Progress'}</strong>
                <br />
                {item.bg === 'success' && <>Score: <strong>{item.score}%</strong></>}
              </div>
            </Tooltip>
          }>
          <div
            key={idx}
            className={`text-white fs-7 fw-bold d-flex justify-content-center align-items-center overlaped-circle bg-gradient-${item.bg} rounded-circle shadow-sm cursor-pointer`}
            style={{
              marginLeft: idx === 0 ? 0 : -8,
              zIndex: items.length - idx,
            }}
          >
            {item.initials}
          </div>

        </OverlayTrigger>
      ))}
      {extraCount > 0 && (
        <div
          className="text-gray-700 bg-secondary fs-6 fw-bold d-flex justify-content-center align-items-center rounded-circle shadow-sm cursor-pointer"
          style={{
            width: 32,
            height: 32,
            border: '2px solid white',
            marginLeft: -10,
            userSelect: 'none',
            zIndex: 0,
          }}
        >
          +{extraCount}
        </div>
      )}
    </div>
  );
}

const SingleProjectStageGateCard: React.FC<Props> = ({
  project,
  stageGateInfo,
  stages,
  setShowManageEvaluator,
  summeryData
}) => {
  const { goBack } = useSmartNavigation();
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [comingSoonModal, setComingSoonModal] = useState<boolean>(false);
  const [comingSoonTitle, setComingSoonTitle] = useState<string>('');
  const getStageStatus = useCallback((stageData: any): Status => {
    const completed = stageData.dk_stage_status === stageCompletedCode;
    if (completed && stageData?.active_gate === 0) return "completed";
    if ((stageData?.dk_stage_status === "stage_status_1" || stageData?.dk_stage_status === "stage_status_2") && stageData?.active_gate === 1) return "current";
    return "upcoming";
  }, []);

  const [showManageTeamMembersModal, setShowManageTeamMembersModal] = useState<boolean>(false);

  const { can, canStage } = usePermission();
  const stagesList = useMemo(() => Object.entries(stages), [stages]);

  const isStage4Completed = stages.find((stage) => stage.dk_stage === "project_stage_4")?.dk_stage_status==="stage_status_4";
  const evaluatorList = useMemo(() => summeryData && summeryData.evaluation_by_evaluator ? summeryData.evaluation_by_evaluator.map((item: any) => item) : [], [summeryData]);

  const getStageColor = useCallback(
    (index: number) => stageColors[index % stageColors.length],
    []
  )

  const handleDownloadBusinessCaseReport = async () => {
      const newTab = window.open("/report-loading.html", "_blank");
      if (!newTab) {
          return;
      }
      try {
          const response = await downloadBusinessCaseReport(project.project_id);
          if (response.status !== "ok") {
              throw new Error(response.message);
          }
          newTab.location.replace(response.data.report_url);
      } catch (error) {
          newTab.close();
          showSnackbar(getApiErrorMessage(error, "Failed to download business case report."),
              "danger"
          );
      }
  };
  const handleDownloadElevatorPitch = () => {
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    const url = `${base}/manage-project/single-page-report/${project.project_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const elevatorPitchLink = (
    <Link
      to={isStage4Completed ? `/manage-project/elevator-pitch/${project.project_id}${location.search}` : "#"}
      state={buildNavigationState(location.state?.from)}
      className={`w-100 hover-lift ${isStage4Completed ? "cursor-pointer" : "cursor-none opacity-75"}`}
      aria-disabled={!isStage4Completed}
      onClick={(event) => {
        if (!isStage4Completed) {
          event.preventDefault();
        }
      }}
    >
      <Card className="shadow-sm w-100">
        <CardBody className="p-3 d-flex justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <CircleDot size={20} className="text-primary" />
            <div className="fw-bold text-gray-700">Draft Elevator Pitch</div>
          </div>
          <ArrowRight size={18} className="text-primary" />
        </CardBody>
      </Card>
    </Link>
  );

  return (
    <div className="card border-0 shadow-lg rounded-4 overflow-hidden">
      {/* Project Header */}
      <div className="p-3 p-md-4 border-bottom">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-2 gap-md-3">
            <div className="bg-primary bg-opacity-10 p-2 p-md-3 rounded-3">
              <Layers size={24} className="text-primary" />
            </div>
            <div>
              <h3 className="fw-bold text-dark mb-0 fs-5 fs-md-4">{project.name}</h3>
              <p className="text-muted mb-0">FIDPM Stage Gate</p>
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-light-primary" onClick={() => goBack('/dashboard/stagegates')}>
            <ChevronLeft size={16} className="me-1 fs-4" />
            <span className="d-none d-sm-inline">Back</span>
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="card-body p-0 p-md-4 bg-light row flex-sm-row-reverse">
        <div className="row  h-100 col-12 col-xl-4 g-4 mt-0">
          {/* Project Details */}
          <div className="col-12 col-md-6 col-xl-12 my-0 d-flex">
            <div className="card bg-primary bg-opacity-5 w-100">
              <div className="card-body p-6">
                <div className="border-bottom pb-3">
                  <h6 className="fw-bold mb-0">Project Details</h6>
                </div>
                <div className="d-flex flex-column flex-wrap gap-3 mt-3">
                  {/* Project Name Card */}
                  {/* <Card className="shadow-sm w-100">
                    <CardBody className="p-3 d-flex justify-content-between align-items-center gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <div className="rounded-3">
                          <FolderKanban size={20} className="text-primary" />
                        </div>
                        <div className="fw-bold text-gray-700">{project.name}</div>
                      </div>
                    </CardBody>
                  </Card> */}

                  {/* Department / Sector / Location Table */}
                  <Card className="shadow-sm w-100">
                    <CardBody className="p-3">
                      <table className="table table-borderless table-sm mb-0">
                        <tbody>
                          {project.department_name && (
                            <tr>
                              <td className="fw-bold d-flex justify-content-between align-items-center gap-2 text-gray-700">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="bg-gray-700 bg-opacity-10 p-2 p-md-3 rounded-3">
                                    <Building size={18} className="text-gray-700" />
                                  </div>
                                  <div >
                                    <div className="fw-bold text-gray-700">Department</div>
                                    <div className="fw-bold fs-6 text-dark">{project.department_name}</div>
                                  </div>
                                </div>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id="location-tooltip">
                                      Geo Location
                                    </Tooltip>
                                  }
                                >
                                  <p className="mb-0 geolocation-btn cursor-pointer geolocation-btn-text" onClick={() => setShowModal(true)}>
                                    <MapPin size={18} className="geolocation-btn" />Geo Location
                                  </p>
                                </OverlayTrigger>
                              </td>
                            </tr>
                          )}
                          {project.sector_name && (
                            <tr>
                              <td className="w-100">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="bg-info bg-opacity-10 p-2 p-md-3 rounded-3">
                                    <Grid2X2 size={18} className="text-info" />
                                  </div>
                                  <div >
                                    <div className="fw-bold text-gray-700">Sector</div>
                                    <div className="fw-bold fs-6 text-dark">{project.sector_name}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          {project.district_name && (
                            <tr>
                              <td className="fw-bold d-flex justify-content-between align-items-center gap-2 text-gray-700">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="bg-danger bg-opacity-10 p-2 p-md-3 rounded-3">
                                    <Building2 size={18} className="text-danger" />
                                  </div>
                                  <div>
                                    <div className="fw-bold text-gray-700">District Municipality</div>
                                    <div className="fw-bold fs-6 text-dark">{project.district_name}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          {project.municipality_name && (
                            <tr>
                              <td className="fw-bold d-flex justify-content-between align-items-center gap-2 text-gray-700">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="bg-success bg-opacity-10 p-2 p-md-3 rounded-3">
                                    <Goal size={18} className="text-success" />
                                  </div>
                                  <div>
                                    <div className="fw-bold text-gray-700">Local Municipality</div>
                                    <div className="fw-bold fs-6 text-dark">{project.municipality_name}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          {project.project_owner && (
                            <tr>
                              <td className="fw-bold text-gray-700 d-flex justify-content-between align-items-center gap-2">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="bg-warning bg-opacity-10 p-2 p-md-3 rounded-3">
                                    <UserCheck2 size={18} className="text-warning" />
                                  </div>
                                  <div>
                                    <div className="fw-bold text-gray-700">Project Owner</div>
                                    <div className="fw-bold fs-6 text-dark">{project.project_owner}</div>
                                  </div>
                                </div>
                                
                                <PermissionGate allowed={can('project_team', 'view')}>
                                  <Button
                                    variant="info"
                                    size="sm"
                                    onClick={() => setShowManageTeamMembersModal(true)}
                                  >
                                    <KTIcon iconName="profile-user" className="fs-3 me-1 pe-0" />
                                    Project Team
                                  </Button>
                                </PermissionGate>
                              </td>
                            </tr>
                          )}

                          {project.budget && (
                            <tr>
                              <td className="fw-bold text-gray-700">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="bg-success bg-opacity-10 p-2 p-md-3 rounded-3">
                                    <Banknote size={18} className="text-success" />
                                  </div>
                                  <div>
                                    <div className="fw-bold text-gray-700">Approximate Value</div>
                                    <div className="fw-bold fs-6 text-dark">{project.budget}</div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          </div>


          {/* Quick Links */}
          <div className="col-12 mt-4 mt-md-4 order-0 order-md-3 order-xl-0 d-flex">
            <div className="card bg-primary bg-opacity-5 w-100">
              <div className="card-body p-6">
                <div className="border-bottom pb-3 d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold mb-0">Quick Links</h6>
                </div>
                <div className="d-flex flex-column gap-3 mt-3">
                  {!canStage('project_stage_4', 'view') ? (
                    <PermissionGate allowed={false} className="d-block w-100">{elevatorPitchLink}</PermissionGate>
                  ) : isStage4Completed ? elevatorPitchLink : (
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="elevator-pitch-disabled-tooltip">
                          Elevator pitch is available only after Stage 4 is completed.
                        </Tooltip>
                      }
                    >
                      <span className="d-block w-100">{elevatorPitchLink}</span>
                    </OverlayTrigger>
                  )}
                  {["Feasibility Tool", "Request for Tender", "Change Request"].map((link, idx) => (
                    <Link to="#" key={idx} className="w-100 hover-lift" onClick={() => { setComingSoonModal(true); setComingSoonTitle(link); }}>
                      <Card className="shadow-sm w-100">
                        <CardBody className="p-3 d-flex justify-content-between align-items-center gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <CircleDot size={20} className="text-primary" />
                            <div className="fw-bold text-gray-700">{link}</div>
                          </div>
                          <ArrowRight size={18} className="text-primary" />
                        </CardBody>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Evaluation Summary */}
          <div className="col-12 col-md-6 col-xl-12 mt-4 mt-md-0 order-1 mt-xl-4 d-flex">
            <div className="card bg-primary bg-opacity-5 w-100">
              <div className="card-body p-6">
                <div className="border-bottom pb-3 d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold mb-0">Evaluation Summary</h6>
                  {/* <span
                    className="fw-bold text-primary cursor-pointer"
                    onClick={() => setShowManageEvaluator(true)}
                  >
                    More Details
                  </span> */}
                </div>
                {summeryData ? <div className="d-flex flex-column gap-3 mt-3">
                  {/* Evaluators Card */}
                  <Card className="shadow-sm w-100">
                    <CardBody className="p-3 d-flex justify-content-between align-items-center gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <Users size={20} className="text-primary" />
                        <div className="fw-bold text-gray-700">Evaluators</div>
                      </div>
                      <StackedAvatarList items={evaluatorList} />
                    </CardBody>
                  </Card>

                  {/* Status Card */}
                  <Card className="shadow-sm w-100">
                    <CardBody className="p-3 d-flex flex-column gap-3 pb-0">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <Info size={18} className="text-info" />
                          <div className="fw-bold text-gray-700">Status</div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          {summeryData.project_evaluation_status === "Pending" && <div className="d-flex align-items-center rounded-3 px-3 py-1" style={{ background: "#FFF4E8", fontWeight: 600 }}>
                            <span style={{ color: "#B45309" }}>Pending</span>
                          </div>}
                          {summeryData.project_evaluation_status === "Completed" && <div className="d-flex align-items-center rounded-3 px-3 py-1" style={{ background: "#EAF7EF", fontWeight: 600 }}>
                            <span style={{ color: "#15803D" }}>Completed</span>
                          </div>}
                          {summeryData.project_evaluation_status === "In Progress" && <div className="d-flex align-items-center rounded-3 px-3 py-1" style={{ background: "#eaf0f7", fontWeight: 600 }}>
                            <span style={{ color: "#1c4c8b" }}>In Progress</span>
                          </div>}
                        </div>
                      </div>
                      {/* Score */}
                      <div className="d-flex flex-column gap-2 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <Goal size={18} className="text-danger" />
                            <div className="fw-bold text-gray-700">Score</div>
                          </div>
                          <div className={`${Number(summeryData.project_evaluation_percentage) < 50 ? "text-danger" : Number(summeryData.project_evaluation_percentage) < 80 ? "text-warning" : "text-success"} fw-bold`}>{summeryData.project_evaluation_percentage}%</div>
                        </div>
                        <div className="progress-bar-custom w-100">
                          <div className="progress-fill" style={{ width: `${summeryData.project_evaluation_percentage}%` }} />
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Investor Ready */}
                  <Card className="shadow-sm w-100">
                    <CardBody className="p-3 d-flex justify-content-between align-items-center gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <ShieldCheck size={20} className="text-success" />
                        <div className="fw-bold text-gray-700">Investor Ready</div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {summeryData.is_invest_ready === 1 ? <>
                          <div className="rounded-4 bg-success bg-opacity-10 p-1">
                            <CheckCircle size={18} className="text-success" />
                          </div>
                          <div className="fw-bold text-gray-700">Yes</div></> : <>
                          <div className="rounded-4 bg-danger bg-opacity-10 p-1">
                            <XCircle size={18} className="text-danger" />
                          </div>
                          <div className="fw-bold text-gray-700">No</div>
                        </>
                        }
                      </div>
                    </CardBody>
                  </Card>
                </div> :
                  <div className="d-flex flex-column align-items-center gap-3 mt-3">
                    <div className="d-flex align-items-center gap-2">
                      <Info size={18} className="text-info" />
                      <div className="fw-bold text-gray-700">No evaluation summary available.</div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className="position-relative col-12 col-xl-8 mt-4 mt-xl-0 order-1 order-xl-0">
          {stagesList.map(([index, stage], idx, array) => {
            const stageData: any = stage;
            const gateNum = parseInt(index, 10);
            const gateKey = gateNum + 1;
            const status = getStageStatus(stageData);
            const style = statusConfig[status];
            const StatusIcon = style.icon;
            const GateIcon = stageGateInfo[gateKey]?.icon || FileText;
            const isAccessible = canStage(stageData.dk_stage, 'view');
            const isLast = idx === array.length - 1;
            const color = getStageColor(idx);

            return (
              <div key={gateNum} className="position-relative mb-4">
                <div className="d-flex">
                  <div className="position-relative d-none d-md-block" style={{ width: '75px' }}>
                    <HorizontalConnector color={style.color} />
                    <TimelineCircle color={style.color} icon={StatusIcon} />
                    {!isLast && (
                      <div
                        className="position-absolute d-none d-md-block"
                        style={{
                          width: '2px',
                          height: 'calc(100% + 15px)',
                          background: style.color,
                          transform: 'translateX(-50%)',
                          left: '21px',
                          top: '50%',
                          zIndex: 1,
                          opacity: 0.3
                        }}
                      />
                    )}
                  </div>

                  <div className="w-100 ps-0">
                    <div className={`card border-0 ${status === "current" ? "active-gate bg-light-primary" : "hover-lift"} rounded-4`}>
                      <div className="card-body p-3 p-md-4 position-relative">
                        <div className="position-absolute top-0 end-0 p-3">
                          <StatusBadge badgeClass={style.badgeClass} label={style.label} />
                        </div>

                        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 w-100">
                          <StageNumberBox
                            icon={GateIcon}
                            color={color}
                            gateNum={gateNum}
                            lightBg={`${color}15`}
                            mediumBg={`${color}30`}
                          />

                          <div className="flex-grow-1 w-100">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center w-75">
                              <PermissionGate allowed={isAccessible} className="d-inline-flex align-items-center">
                                <Link
                                  state={{ from: location.state?.from || location.pathname }}
                                  to={`${getStageRedirect(project.project_id, stageData.dk_stage)}/${location?.search}`}
                                  className="text-decoration-none flex-shrink-0 d-inline-flex align-items-center mb-1 mb-lg-0"
                                >
                                  <h4 className="fw-bold mb-0" style={{ color: "#073d8d" }}>
                                    {stageData.dn_stage}
                                  </h4>
                                  <ChevronRight size={18} className="ms-2" style={{ color: "#073d8d" }} />
                                </Link>
                                {stage.dk_stage==='project_stage_4' && <PermissionGate allowed={can('reports', 'view')}><button className="btn btn-link text-primary ms-2" onClick={handleDownloadBusinessCaseReport}>Draft Prospectus</button></PermissionGate>}
                                {stage.dk_stage==='project_stage_4'  &&  isStage4Completed && <button className="btn btn-link text-primary ms-4" onClick={handleDownloadElevatorPitch}>Draft Elevator Pitch Report</button>}
                              </PermissionGate>

                              {/* Single responsive Actions */}
                              {/* <div className="d-flex flex-wrap gap-2 mt-2 mt-lg-1 mt-xl-0">
                                <Actions stageData={stageData} project={project} status={status} />
                              </div> */}
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <CommonModal
        showModal={comingSoonModal}
        setShowModal={setComingSoonModal}
        title={comingSoonTitle}
      />

      <GeoLocationPopUp showModal={showModal} setShowModal={setShowModal} selectedProject={{ project_id: project.project_id, name: project.name, budget: project.budget as string }} />

      {/* The modal loads its data on mount, so only mount it for users who may view the team. */}
      {can('project_team', 'view') && (
        <Suspense fallback={null}>
          <ManageTeamMembersModal
            show={showManageTeamMembersModal}
            onHide={() => setShowManageTeamMembersModal(false)}
            project_id={project.project_id}
            disableFormFields={!can('project_team', 'write')}
          />
        </Suspense>
      )}
    </div>
  );
};

export default SingleProjectStageGateCard;